/**
 * Client event emitter (RFC-0974 §6.3).
 *
 * Best-effort, never-raise, env-gated, consent-gated. Producers call the typed
 * helpers in `events.ts`; this module owns the bounded queue, the consent re-check
 * before every send, the drain-on-withdrawal, and the POST to the collector's
 * public path (a separate origin — so NOT via `apiFetch`, which would prepend the
 * API base and leak the affinity cookie).
 *
 * Delivery is fire-and-forget: a failed send is retried a bounded number of times
 * and then dropped (no durable queue — the collector + BigQuery own dedup).
 */

import { getAuthToken } from '../api/client'
import { createNadDebug } from '../utils/debugLog'
import { isAnalyticsAllowed, registerWithdrawHandler } from './consentGate'
import { readErrorName } from './errorClass'
import { clearSessionId, getSessionId } from './sessionId'

const log = createNadDebug('dw:analytics-events:emit')

/** Event property values are minimized primitives — never objects/PII. */
export type EventPropValue = string | number | boolean | undefined
export type EventProps = Record<string, EventPropValue>

export interface EventEnvelope {
  event_type: string
  event_id: string
  event_version: number
  timestamp: string
  session_id: string
  properties: EventProps
  /** True ⇒ attach the GoTrue JWT; the collector derives user_id from it. */
  authenticated: boolean
}

interface QueueItem {
  envelope: EventEnvelope
  attempts: number
}

interface EmitterConfig {
  collectorUrl: string
  maxQueue: number
  maxAttempts: number
  getToken: () => Promise<string | null>
  fetchImpl: typeof fetch
  /**
   * Beacon transport for anonymous page-hide events (INP, §5.6). Returns true if
   * the beacon was queued by the browser. Injectable for tests. Defaults to
   * `navigator.sendBeacon` when available, else null (⇒ keepalive-fetch fallback).
   */
  sendBeaconImpl: ((url: string, body: Blob) => boolean) | null
}

/**
 * Dev-only override so a local console can send events through a locally run
 * collector (scripts/analytics-e2e/harness.py) without a rebuild — the same
 * pattern as `dreamwright_api_base`. Ignored in production builds
 * (`import.meta.env.DEV` is false), so it can never redirect real traffic.
 */
export const DEV_COLLECTOR_URL_KEY = 'dreamwright_events_collector_url'

function readCollectorUrl(): string {
  try {
    const configured = (import.meta.env.VITE_EVENTS_COLLECTOR_URL || '').trim()
    if (configured || !import.meta.env.DEV) return configured
    if (typeof window === 'undefined' || !window.localStorage) return ''
    return (window.localStorage.getItem(DEV_COLLECTOR_URL_KEY) || '').trim()
  } catch {
    return ''
  }
}

const config: EmitterConfig = {
  collectorUrl: readCollectorUrl(),
  maxQueue: 200,
  maxAttempts: 3,
  // Lazy: only touch the api/client binding when a token is actually needed, so
  // importing this module never hard-requires that export (keeps partial
  // `vi.mock('../api/client')` tests that don't stub getAuthToken working).
  getToken: () => getAuthToken(),
  fetchImpl: (...args) => globalThis.fetch(...args),
  sendBeaconImpl:
    typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
      ? (url, body) => navigator.sendBeacon(url, body)
      : null,
}

const queue: QueueItem[] = []
let flushScheduled = false
// Bumped by severSession(). An in-flight delivery captures the generation at
// dequeue and aborts before fetch() if it changed — so a sign-out / consent
// withdrawal cancels items already awaiting the network, not just queued ones.
let deliveryGeneration = 0

/** Simple metadata-only counters (never payloads) — surfaced for tests/debug. */
export const emitterStats = {
  emitted: 0,
  sent: 0,
  dropped_no_op: 0,
  dropped_consent: 0,
  dropped_overflow: 0,
  dropped_exhausted: 0,
  beacon_sent: 0,
  beacon_dropped: 0,
}

function enabled(): boolean {
  return Boolean(config.collectorUrl)
}

function newEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/**
 * Enqueue an event for best-effort delivery.
 *
 * @returns false if the event was not enqueued (disabled / consent / overflow).
 */
export function emitEvent(
  eventType: string,
  authenticated: boolean,
  properties: EventProps = {}
): boolean {
  try {
    if (!enabled()) {
      emitterStats.dropped_no_op++
      log({ event: 'dev-no-op', event_type: eventType })
      return false
    }
    // Consent re-checked at emit AND again immediately before each send (§6.3).
    if (!isAnalyticsAllowed()) {
      emitterStats.dropped_consent++
      log({ event: 'consent-suppressed', event_type: eventType })
      return false
    }
    const envelope: EventEnvelope = {
      event_type: eventType,
      event_id: newEventId(),
      event_version: 1,
      timestamp: new Date().toISOString(),
      session_id: getSessionId(),
      properties: pruneUndefined(properties),
      authenticated,
    }
    if (queue.length >= config.maxQueue) {
      // Shed the oldest to bound memory; newest signal is the most useful.
      queue.shift()
      emitterStats.dropped_overflow++
      log({ event: 'drop', reason: 'overflow' })
    }
    queue.push({ envelope, attempts: 0 })
    emitterStats.emitted++
    log({ event: 'enqueue', event_type: eventType, queue_len: queue.length })
    scheduleFlush()
    return true
  } catch (err) {
    // Never let telemetry break the product. Log only the error class name.
    log({ event: 'emit-error', error_name: readErrorName(err instanceof Error ? err : undefined) })
    return false
  }
}

function pruneUndefined(props: EventProps): EventProps {
  const out: EventProps = {}
  for (const [k, v] of Object.entries(props)) {
    if (v !== undefined) out[k] = v
  }
  return out
}

function scheduleFlush(): void {
  if (flushScheduled) return
  flushScheduled = true
  // Microtask/timer defer so emit() returns immediately (fire-and-forget).
  setTimeout(() => {
    flushScheduled = false
    void flush()
  }, 0)
}

async function flush(): Promise<void> {
  if (!enabled()) {
    queue.length = 0
    return
  }
  // Snapshot + clear; failed transient items are re-queued below.
  const batch = queue.splice(0, queue.length)
  // Capture the sever generation for this batch — if a sign-out / withdrawal
  // bumps it while we're awaiting the network, in-flight items are cancelled.
  const gen = deliveryGeneration
  for (const item of batch) {
    // Re-check consent immediately before each send: a withdrawal mid-flush
    // suppresses everything still pending (§6.3).
    if (!isAnalyticsAllowed()) {
      log({ event: 'consent-suppressed', event_type: item.envelope.event_type, phase: 'flush' })
      continue
    }
    const status = await send(item.envelope, gen)
    if (status === 'sent') {
      emitterStats.sent++
      // The session id is STABLE within a browsing session (it is NOT rotated
      // per authenticated event — that would fragment the sign-in→product-action
      // funnel). The anon→user stitch is achieved by the authenticated event
      // carrying both the (stable) session_id and the server-derived user_id.
      // Rotation happens only at genuine session end: sign-out, consent
      // withdrawal (severSession), or expiry (getSessionId).
      continue
    }
    if (status === 'drop') {
      // Terminal non-delivery (no token / consent withdrawn mid-flight /
      // permanent 4xx). NOT counted as sent and NOT rotating the session, so a
      // later valid authenticated event can still perform the stitch.
      continue
    }
    // status === 'retry' (timeout / 5xx / 429 / network)
    item.attempts++
    if (item.attempts >= config.maxAttempts) {
      emitterStats.dropped_exhausted++
      log({ event: 'drop', reason: 'exhausted', event_type: item.envelope.event_type })
      continue
    }
    // Bounded admission on requeue too: during a sustained outage, new emits can
    // refill the queue while this batch is in flight — re-pushing failed items
    // unconditionally would exceed maxQueue and defeat load-shedding.
    if (queue.length >= config.maxQueue) {
      emitterStats.dropped_overflow++
      log({ event: 'drop', reason: 'overflow', phase: 'requeue' })
      continue
    }
    queue.push(item)
  }
  if (queue.length > 0) scheduleFlush()
}

type SendStatus = 'sent' | 'drop' | 'retry'

async function send(envelope: EventEnvelope, gen: number): Promise<SendStatus> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (envelope.authenticated) {
      const token = await config.getToken()
      if (!token) {
        // Authenticated event with no token — the collector would reject it.
        // Terminal DROP (not a delivery): don't count it sent or rotate/stitch,
        // so a later valid-token event can still perform the anon→user stitch.
        return 'drop'
      }
      headers.Authorization = `Bearer ${token}`
    }
    // Final gates AFTER all awaits (the token fetch above), immediately before
    // the network call: a consent withdrawal OR a session sever (sign-out) can
    // land while getToken() is in flight — this already-dequeued item must not
    // reach the network. Terminal drop — do not retry a suppressed event.
    if (!isAnalyticsAllowed()) return 'drop'
    if (gen !== deliveryGeneration) return 'drop'
    const { authenticated: _auth, ...wire } = envelope
    const res = await config.fetchImpl(`${config.collectorUrl}/ingest/public`, {
      method: 'POST',
      headers,
      body: JSON.stringify(wire),
      // Separate origin: never send the affinity cookie to the collector.
      credentials: 'omit',
      keepalive: true,
    })
    // 2xx/3xx (incl. 202 accepted / accepted-and-dropped) = delivered from the
    // client's perspective. 5xx/429/network are transient → retry.
    if (res.status >= 200 && res.status < 400) return 'sent'
    if (res.status >= 500 || res.status === 429) return 'retry'
    return 'drop' // 4xx (other than 429): permanent, do not retry
  } catch {
    return 'retry' // network error — transient
  }
}

/** Drop all queued events (consent withdrawal — §6.3). Called by the gate. */
export function drainQueue(): void {
  const n = queue.length
  queue.length = 0
  if (n > 0) log({ event: 'drain', dropped: n })
}

/**
 * Sever the analytics session: cancel in-flight deliveries (via the generation
 * bump), drop all queued events, and CLEAR the stored session id WITHOUT minting
 * a new one — a user who withdrew consent must not receive a fresh persistent
 * identifier. A new id is created lazily only on a later consented emit
 * (`getSessionId`). Used for both consent withdrawal and sign-out.
 */
export function severSession(): void {
  deliveryGeneration++
  drainQueue()
  clearSessionId()
}

// A consent withdrawal severs the session (drain + rotate).
registerWithdrawHandler(severSession)

// ─── Anonymous page-hide beacon (INP, §2 / §5.6) ────────────────────────────
//
// Delivered via navigator.sendBeacon with a CORS-safelisted `text/plain` Blob
// body so a page-hide POST needs NO preflight and carries NO Authorization
// header. The collector parses the raw JSON regardless of Content-Type. The
// beacon envelope carries session_id only — NEVER a user_id (INP is not joined
// to users/projects, §4). Best-effort and never-throw, like the fetch path.

/**
 * Emit an anonymous event via sendBeacon (page-hide). Returns false if not sent
 * (disabled / consent / transport unavailable). Never throws.
 */
export function emitBeacon(eventType: string, properties: EventProps = {}): boolean {
  try {
    if (!enabled()) {
      emitterStats.dropped_no_op++
      log({ event: 'beacon-dev-no-op', event_type: eventType })
      return false
    }
    if (!isAnalyticsAllowed()) {
      emitterStats.dropped_consent++
      log({ event: 'beacon-consent-suppressed', event_type: eventType })
      return false
    }
    // Wire shape mirrors the fetch path MINUS `authenticated` — anonymous, so no
    // token is fetched and no user_id is derived. session_id keys the report.
    const wire = {
      event_type: eventType,
      event_id: newEventId(),
      event_version: 1,
      timestamp: new Date().toISOString(),
      session_id: getSessionId(),
      properties: pruneUndefined(properties),
    }
    const url = `${config.collectorUrl}/ingest/public`
    const json = JSON.stringify(wire)
    // text/plain Blob ⇒ CORS-safelisted request, no page-hide preflight.
    const blob = new Blob([json], { type: 'text/plain' })
    if (config.sendBeaconImpl) {
      const ok = config.sendBeaconImpl(url, blob)
      if (ok) {
        emitterStats.beacon_sent++
        log({ event: 'beacon-sent', event_type: eventType })
        return true
      }
      // sendBeacon refused (queue full / too large) — fall through to fetch.
    }
    // Fallback: keepalive fetch with the SAME safelisted content-type and NO
    // Authorization header, credentials omitted (separate collector origin).
    void config
      .fetchImpl(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: json,
        credentials: 'omit',
        keepalive: true,
      })
      .catch(() => {
        /* best-effort — a failed page-hide beacon is simply dropped */
      })
    emitterStats.beacon_sent++
    log({ event: 'beacon-sent-fetch-fallback', event_type: eventType })
    return true
  } catch (err) {
    emitterStats.beacon_dropped++
    // Log only the error class name — never String(err) (may carry a URL/message).
    log({ event: 'beacon-error', error_name: readErrorName(err instanceof Error ? err : undefined) })
    return false
  }
}

// ─── Page lifecycle: page-hide flush of the authenticated queue ──────────────
//
// The authenticated queue is flushed via keepalive fetch. Without a page-hide
// trigger, an abrupt tab close can strand queued events. `initEventLifecycle`
// installs a single `visibilitychange` listener (idempotent across StrictMode /
// HMR) that flushes the queue when the document becomes hidden. `teardown()`
// removes it (used by HMR disposal + tests). The INP page-hide REPORT itself is
// driven by web-vitals in `useAppHealthAnalytics`, not here.

let lifecycleInstalled = false
let lifecycleTeardown: (() => void) | null = null

function onVisibilityHidden(): void {
  try {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      void flush()
    }
  } catch {
    /* never let a lifecycle handler throw */
  }
}

/** Install the page-hide flush listener once (idempotent). */
export function initEventLifecycle(): void {
  if (lifecycleInstalled) return
  if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return
  lifecycleInstalled = true
  document.addEventListener('visibilitychange', onVisibilityHidden)
  lifecycleTeardown = () => {
    document.removeEventListener('visibilitychange', onVisibilityHidden)
  }
  log({ event: 'lifecycle-installed' })
}

/** Remove the lifecycle listener (HMR disposal / tests). */
export function _teardownEventLifecycle(): void {
  if (lifecycleTeardown) lifecycleTeardown()
  lifecycleTeardown = null
  lifecycleInstalled = false
}

// HMR: tear down the old module's listener before the replacement installs its
// own, so a hot reload never leaks a duplicate visibilitychange handler.
if (import.meta.hot) {
  import.meta.hot.dispose(() => _teardownEventLifecycle())
}

/** Test/wiring hook: override config (collector URL, fetch, token getter). */
export function configureEmitter(partial: Partial<EmitterConfig>): void {
  Object.assign(config, partial)
}

/** Test helper — reset queue + counters + lifecycle listeners. */
export function _resetEmitterForTest(): void {
  queue.length = 0
  flushScheduled = false
  emitterStats.emitted = 0
  emitterStats.sent = 0
  emitterStats.dropped_no_op = 0
  emitterStats.dropped_consent = 0
  emitterStats.dropped_overflow = 0
  emitterStats.dropped_exhausted = 0
  emitterStats.beacon_sent = 0
  emitterStats.beacon_dropped = 0
  _teardownEventLifecycle()
}

/** Test helper — force a synchronous flush and await it. */
export async function _flushForTest(): Promise<void> {
  await flush()
}

/** Test helper — the current delivery generation (bumped by severSession). */
export function _deliveryGenerationForTest(): number {
  return deliveryGeneration
}

/** Test helper — current queue depth. */
export function _queueDepthForTest(): number {
  return queue.length
}
